import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { feedbackService } from '../../services/feedbackService.js';

const Feedback = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length > 100) {
      errors.name = 'Name must be 100 characters or less';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (formData.email.trim().length > 100) {
      errors.email = 'Email must be 100 characters or less';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Subject validation (now required)
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (formData.subject.trim().length > 150) {
      errors.subject = 'Subject must be 150 characters or less';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length > 3000) {
      errors.message = 'Message must be 3000 characters or less';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    // Validate form
    if (!validateForm()) {
      setSubmitStatus('validation_error');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Submit feedback via API with exact schema format
      const response = await feedbackService.submitFeedback({
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim()
      });
      
      // ...existing code...
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setSubmitStatus(null);
        setValidationErrors({});
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="feedback-page">
      <div className="feedback-container">
        {/* Hero Section */}
        <div className="feedback-hero">
          <div className="feedback-hero-background">
            <div className="hero-gradient-orb"></div>
            <div className="hero-floating-particles">
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="particle"></div>
            </div>
          </div>
          
          <div className="feedback-hero-content">
            <div className="feedback-badge">
              <i className="fas fa-comment-dots"></i>
              <span>We Value Your Input</span>
            </div>
            <h1 className="feedback-title">Share Your Feedback</h1>
            <p className="feedback-subtitle">
              Help us improve DevRoom by sharing your thoughts, reporting bugs, or suggesting new features
            </p>
          </div>
        </div>

        {/* Feedback Form */}
        <div className="feedback-form-section">
          <form onSubmit={handleSubmit} className="feedback-form">
            {/* Personal Information */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">
                  <i className="fas fa-user"></i>
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  maxLength="100"
                  placeholder="Your full name"
                  className={validationErrors.name ? 'error' : ''}
                />
                {validationErrors.name && (
                  <div className="field-error">
                    <i className="fas fa-exclamation-triangle"></i>
                    {validationErrors.name}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="email">
                  <i className="fas fa-envelope"></i>
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  maxLength="100"
                  placeholder="your.email@example.com"
                  className={validationErrors.email ? 'error' : ''}
                />
                {validationErrors.email && (
                  <div className="field-error">
                    <i className="fas fa-exclamation-triangle"></i>
                    {validationErrors.email}
                  </div>
                )}
              </div>
            </div>

            {/* Subject */}
            <div className="form-group">
              <label htmlFor="subject">
                <i className="fas fa-tag"></i>
                Subject *
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                maxLength="150"
                placeholder="Brief summary of your feedback"
                className={validationErrors.subject ? 'error' : ''}
              />
              {validationErrors.subject && (
                <div className="field-error">
                  <i className="fas fa-exclamation-triangle"></i>
                  {validationErrors.subject}
                </div>
              )}
            </div>

            {/* Message */}
            <div className="form-group">
              <label htmlFor="message">
                <i className="fas fa-comment"></i>
                Your Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                maxLength="3000"
                rows="6"
                placeholder="Please provide detailed feedback, suggestions, or describe any issues you've encountered..."
                className={validationErrors.message ? 'error' : ''}
              ></textarea>
              {validationErrors.message && (
                <div className="field-error">
                  <i className="fas fa-exclamation-triangle"></i>
                  {validationErrors.message}
                </div>
              )}
              <div className="character-count">
                {formData.message.length}/3000 characters
              </div>
            </div>

            {/* Submit Section */}
            <div className="form-actions">
              {submitStatus === 'success' && (
                <div className="status-message success">
                  <i className="fas fa-check-circle"></i>
                  <span>Thank you! Your feedback has been submitted successfully. We'll review it and get back to you if needed.</span>
                </div>
              )}
              
              {submitStatus === 'validation_error' && (
                <div className="status-message error">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>Please correct the errors above and try again.</span>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="status-message error">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>Failed to submit feedback. Please check your connection and try again.</span>
                </div>
              )}

              <div className="action-buttons">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="btn-secondary"
                >
                  <i className="fas fa-arrow-left"></i>
                  <span>Go Back</span>
                </button>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      <span>Send Feedback</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
